
import { proxyFetch } from './proxyFetch';
import { ServiceType } from './types';
import { isProxyEnabledSync } from '@/config/proxy';
import { buildHealthCheckUrl, SUPABASE_ANON_KEY } from '@/integrations/supabase/urlBuilder';

/**
 * Упрощенная проверка доступности сервисов с использованием URL builder
 */

// Интерфейс для конфигурации тестов
interface TestConfig {
  url: string;
  headers: Record<string, string>;
  method?: 'GET' | 'POST';
  body?: string;
}

// Статические тестовые конфигурации для каждого сервиса
const STATIC_TEST_CONFIGS: Record<ServiceType, TestConfig> = {
  supabase: {
    url: buildHealthCheckUrl('api'),
    headers: {
      'apikey': SUPABASE_ANON_KEY
    },
    method: 'GET'
  },
  supabaseStorage: {
    url: buildHealthCheckUrl('storage'),
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
    },
    method: 'GET'
  },
  googleMaps: {
    url: 'https://maps.googleapis.com/maps/api/staticmap?size=1x1&key=AIzaSyCoCNFQhyg8mpTmesOi_8ue_SxwliHoRFY',
    headers: {},
    method: 'GET'
  },
  geocoding: {
    url: 'https://maps.googleapis.com/maps/api/geocode/json?address=Moscow&key=AIzaSyCoCNFQhyg8mpTmesOi_8ue_SxwliHoRFY',
    headers: {},
    method: 'GET'
  },
  makeWebhooks: {
    url: 'https://hook.eu2.make.com/nunfd1olha2rxivbibb0vtvsyuka4coy',
    headers: {
      'Content-Type': 'application/json'
    },
    method: 'POST',
    body: JSON.stringify({ test: true, timestamp: Date.now() })
  },
  webhooks: {
    url: 'https://httpbin.org/status/200',
    headers: {},
    method: 'GET'
  }
};

/**
 * Определяет доступность сервиса на основе статуса ответа и ошибок
 */
const isServiceHealthy = (service: ServiceType, status: number, error?: string): boolean => {
  console.log(`🩺 Проверка здоровья ${service}: статус ${status}, ошибка: ${error}`);
  
  // Специальная обработка CORS ошибок для внешних сервисов
  if (error) {
    const errorLower = error.toLowerCase();
    const isCorsError = errorLower.includes('cors') || 
                       errorLower.includes('access-control-allow-origin') ||
                       errorLower.includes('blocked by cors policy');
    
    if (isCorsError) {
      // Для Make.com и Google сервисов CORS означает что сервис работает, но блокирует браузерные запросы
      if (service === 'makeWebhooks' || service === 'googleMaps' || service === 'geocoding') {
        console.log(`✅ ${service} считается доступным (CORS блокировка означает что сервис работает)`);
        return true;
      }
    }
    
    // Обработка сетевых ошибок
    if (errorLower.includes('network') || errorLower.includes('fetch') || errorLower.includes('failed to fetch')) {
      console.log(`❌ ${service} недоступен (сетевая ошибка)`);
      return false;
    }
  }

  // Проверка статусов ответов для каждого сервиса
  switch (service) {
    case 'supabase':
      // Для Supabase API: 200 (OK), 401 (неавторизован), 404 (не найдено) - все означают что API работает
      const supabaseHealthy = status === 200 || status === 401 || status === 404;
      console.log(`📊 Supabase API: статус ${status} = ${supabaseHealthy ? 'здоров' : 'нездоров'}`);
      return supabaseHealthy;
      
    case 'supabaseStorage':
      // Для Supabase Storage: 200, 400 (неверный запрос), 401 (неавторизован), 403 (запрещено), 404 (не найдено)
      const storageHealthy = status === 200 || status === 400 || status === 401 || status === 403 || status === 404;
      console.log(`📦 Supabase Storage: статус ${status} = ${storageHealthy ? 'здоров' : 'нездоров'}`);
      return storageHealthy;
      
    case 'googleMaps':
    case 'geocoding':
      // Для Google APIs: 200 (OK), 400 (неверный запрос), 403 (превышен лимит), 404 (не найдено)
      const googleHealthy = status === 200 || status === 400 || status === 403 || status === 404;
      console.log(`🗺️ Google ${service}: статус ${status} = ${googleHealthy ? 'здоров' : 'нездоров'}`);
      return googleHealthy;
      
    case 'makeWebhooks':
      // Для Make.com: любой HTTP ответ означает что webhook endpoint работает
      // 200 (OK), 404 (не найдено), 405 (метод не поддерживается), 422 (неверные данные)
      const makeHealthy = status > 0 && status !== 0;
      console.log(`🪝 Make.com webhook: статус ${status} = ${makeHealthy ? 'здоров' : 'нездоров'}`);
      return makeHealthy;
      
    case 'webhooks':
      // Для общих webhook: любой положительный статус
      const webhookHealthy = status > 0 && status !== 0;
      console.log(`🔗 Generic webhook: статус ${status} = ${webhookHealthy ? 'здоров' : 'нездоров'}`);
      return webhookHealthy;
      
    default:
      const defaultHealthy = status === 200;
      console.log(`❓ Неизвестный сервис ${service}: статус ${status} = ${defaultHealthy ? 'здоров' : 'нездоров'}`);
      return defaultHealthy;
  }
};

/**
 * Простая проверка доступности сервиса
 */
export const checkSimpleServiceHealth = async (service: ServiceType): Promise<boolean> => {
  try {
    console.log(`🔍 Начинаем простую проверку сервиса: ${service}`);
    
    const testConfig = STATIC_TEST_CONFIGS[service];
    if (!testConfig) {
      console.warn(`❓ Неизвестный сервис для проверки: ${service}`);
      return false;
    }

    const isProxyEnabled = isProxyEnabledSync(service);
    
    console.log(`🔧 Проверка ${service}:`, {
      url: testConfig.url,
      proxyEnabled: isProxyEnabled,
      method: testConfig.method || 'GET',
      hasBody: !!testConfig.body
    });

    const result = await proxyFetch(testConfig.url, {
      service,
      skipProxy: !isProxyEnabled,
      timeout: 8000,
      retryAttempts: 1,
      method: testConfig.method || 'GET',
      headers: testConfig.headers,
      body: testConfig.body
    });
    
    const isHealthy = isServiceHealthy(service, result.status, result.error);
    
    console.log(`📊 Результат простой проверки ${service}:`, {
      url: testConfig.url,
      status: result.status,
      error: result.error,
      healthy: isHealthy,
      proxyUsed: isProxyEnabled
    });
    
    return isHealthy;
  } catch (error) {
    console.log(`💥 Исключение при простой проверке ${service}:`, error);
    
    // Для внешних сервисов CORS ошибки часто означают что сервис работает
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      const isCorsError = errorMessage.includes('cors') || 
                         errorMessage.includes('access-control-allow-origin') ||
                         errorMessage.includes('blocked by cors policy');
      
      if (isCorsError && (service === 'makeWebhooks' || service === 'googleMaps' || service === 'geocoding')) {
        console.log(`✅ ${service} считается доступным (CORS блокировка в catch)`);
        return true;
      }
    }
    
    return false;
  }
};
